import { z } from 'zod'
import { calendarDateSchema } from '../validators/calendar-date.validator.js'
import {
  gallonsPerMinuteSchema,
  horsepowerSchema,
  measuredQuantitySchema,
} from '../validators/decimal-string.validator.js'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { clientTypeSchema } from '../enums/client-type.enum.js'
import { UNPRICEABLE_REASONS } from '../helpers/irrigation-pricing.js'
import { placedPointSchema, siteBoundarySchema } from './geo.dto.js'
import { problemSeveritySchema } from '../enums/problem-severity.enum.js'
import {
  backflowTypeSchema,
  headTypeSchema,
  pipeSizeSchema,
  sensorTypeSchema,
  sourceAbsentEquipmentSchema,
  timerAbsentEquipmentSchema,
  wellComponentTypeSchema,
  type IrrigationEquipmentKind,
} from '../enums/irrigation.enum.js'

/*
 * The irrigation system on a site, as the survey and the desk read it. See
 * docs/irrigation.md. Coordinates cross the wire as numbers and money and
 * measurements as exact decimal strings, as they do for trees.
 */

/**
 * A pin, as every irrigation record but a zone carries one.
 *
 * The same `placedPointSchema` a tree uses. Irrigation used to accept any
 * number for a coordinate and a negative accuracy; a stored pin is a point
 * on the earth, and the write path already refused both.
 */
const positionFields = placedPointSchema.shape

/**
 * A part from the Housecall Pro price book, by value as well as by id, so a
 * pin can draw its label without a second lookup.
 */
export const irrigationPartSummaryDtoSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  partNumber: z.string().nullable(),
  /** True once the material has left the price book. The part is still in the ground. */
  gone: z.boolean(),
})

export type IrrigationPartSummaryDto = z.infer<
  typeof irrigationPartSummaryDtoSchema
>

const waterSourceCommon = {
  id: uuidSchema,
  siteId: uuidSchema,
  label: z.string().nullable(),
  ...positionFields,
  staticPsi: z.number().int().nullable(),
  /** Gallons a minute. See `gallonsPerMinuteSchema`. */
  availableGpm: gallonsPerMinuteSchema.nullable(),
  /**
   * What the technician said this system has none of. Live equipment of a
   * kind outranks it. Defaulted so a phone's copy from before it existed
   * still reads.
   */
  absentEquipment: z.array(sourceAbsentEquipmentSchema).default([]),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}

/**
 * A meter has a size and no pump; a well the other way round.
 *
 * Not `.strict()` for one release. A survey a phone cached still carries the
 * other kind's field, usually as null. Parsing drops that key. A later
 * release can require each kind's own fields only.
 */
export const waterSourceDtoSchema = z.discriminatedUnion('kind', [
  z.object({
    ...waterSourceCommon,
    kind: z.literal('CITY_METER'),
    meterSize: pipeSizeSchema.nullable(),
  }),
  z.object({
    ...waterSourceCommon,
    kind: z.literal('WELL'),
    /** The pump. Null until somebody records it. */
    horsepower: horsepowerSchema.nullable(),
  }),
])

export type WaterSourceDto = z.infer<typeof waterSourceDtoSchema>

export const irrigationTimerDtoSchema = z.object({
  id: uuidSchema,
  siteId: uuidSchema,
  label: z.string().nullable(),
  ...positionFields,
  part: irrigationPartSummaryDtoSchema.nullable(),
  /**
   * The area the timer's pin sits in, computed on read and never stored: an
   * area is a timer's reach, and redrawing one must not leave a stale answer
   * behind. Null when the site has no area around it.
   */
  areaId: uuidSchema.nullable(),
  areaName: z.string().nullable(),
  /**
   * The source its water comes from. Null on a timer recorded before timers
   * had one, until somebody says which. Defaulted, like `absentEquipment`,
   * so a phone's copy from before either existed still reads.
   */
  waterSourceId: uuidSchema.nullable().default(null),
  /** What the technician said this timer has none of. */
  absentEquipment: z.array(timerAbsentEquipmentSchema).default([]),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type IrrigationTimerDto = z.infer<typeof irrigationTimerDtoSchema>

export const zoneHeadCountDtoSchema = z.object({
  type: headTypeSchema,
  count: z.number().int().nonnegative(),
})

export const zoneDtoSchema = z.object({
  id: uuidSchema,
  siteId: uuidSchema,
  timerId: uuidSchema,
  number: z.number().int().positive(),
  boundary: siteBoundarySchema.nullable(),
  headCounts: z.array(zoneHeadCountDtoSchema),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type ZoneDto = z.infer<typeof zoneDtoSchema>

const equipmentCommon = {
  id: uuidSchema,
  siteId: uuidSchema,
  ...positionFields,
  part: irrigationPartSummaryDtoSchema.nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}

/**
 * The valve box something sits in, or null. Defaulted for a survey cached
 * before boxes existed.
 */
const boxFields = { boxId: uuidSchema.nullable().default(null) }

/**
 * One piece of equipment, as its own kind. A valve carries a zone; a sensor
 * does not.
 *
 * Not `.strict()` for one release, for the same reason as a water source: a
 * cached survey still has the other kinds' columns, usually null, and parsing
 * drops them. Size is the stored pipe size, which is wider than the sizes a
 * valve's input now accepts. A well component's source and type, and an
 * other's name, stay nullable: the input requires them, a row or a cache may
 * not have them yet.
 */
export const irrigationEquipmentDtoSchema = z.discriminatedUnion('kind', [
  z.object({
    ...equipmentCommon,
    kind: z.literal('VALVE'),
    ...boxFields,
    zoneId: uuidSchema.nullable(),
    size: pipeSizeSchema.nullable(),
    decoder: irrigationPartSummaryDtoSchema.nullable(),
  }),
  z.object({
    ...equipmentCommon,
    kind: z.literal('VALVE_BOX'),
    spliceCount: z.number().int().nonnegative(),
  }),
  z.object({
    ...equipmentCommon,
    kind: z.literal('MASTER_VALVE'),
    ...boxFields,
    timerId: uuidSchema.nullable(),
    waterSourceId: uuidSchema.nullable(),
    size: pipeSizeSchema.nullable(),
    decoder: irrigationPartSummaryDtoSchema.nullable(),
  }),
  z.object({
    ...equipmentCommon,
    kind: z.literal('BACKFLOW'),
    waterSourceId: uuidSchema.nullable(),
    backflowType: backflowTypeSchema.nullable(),
    size: pipeSizeSchema.nullable(),
    serialNumber: z.string().nullable(),
    /** A calendar date, `YYYY-MM-DD`: a test happens on a day, not at an instant. */
    lastTestedOn: calendarDateSchema.nullable(),
  }),
  z.object({
    ...equipmentCommon,
    kind: z.literal('BALL_VALVE'),
    ...boxFields,
    waterSourceId: uuidSchema.nullable(),
    size: pipeSizeSchema.nullable(),
  }),
  z.object({
    ...equipmentCommon,
    kind: z.literal('SENSOR'),
    ...boxFields,
    timerId: uuidSchema.nullable(),
    sensorType: sensorTypeSchema.nullable(),
  }),
  z.object({
    ...equipmentCommon,
    kind: z.literal('WELL_COMPONENT'),
    waterSourceId: uuidSchema.nullable(),
    wellComponentType: wellComponentTypeSchema.nullable(),
  }),
  z.object({
    ...equipmentCommon,
    kind: z.literal('OTHER'),
    ...boxFields,
    name: z.string().nullable(),
  }),
])

export type IrrigationEquipmentDto = z.infer<
  typeof irrigationEquipmentDtoSchema
>

type EquipmentDtoKind = IrrigationEquipmentDto['kind']

type MissingEquipmentKind = Exclude<IrrigationEquipmentKind, EquipmentDtoKind>

type ExtraEquipmentKind = Exclude<EquipmentDtoKind, IrrigationEquipmentKind>

/**
 * Fails the build when a kind is added to the enum and left out of the
 * union, or added to the union and left out of the enum. A missing kind
 * fails the whole equipment array, and therefore the site.
 */
function assertEquipmentKindsCovered(
  covered: [MissingEquipmentKind, ExtraEquipmentKind] extends [never, never]
    ? true
    : never,
): true {
  return covered
}

assertEquipmentKindsCovered(true)

export const irrigationProblemMaterialDtoSchema = z.object({
  material: irrigationPartSummaryDtoSchema,
  /** Exact decimal string, as the `Decimal(8, 2)` column is sent. */
  quantity: measuredQuantitySchema,
})

/** What a problem is on: exactly one thing. The same kinds as the input. */
export const irrigationProblemSubjectDtoSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('zone'), zoneId: uuidSchema }),
  z.object({ kind: z.literal('timer'), timerId: uuidSchema }),
  z.object({ kind: z.literal('equipment'), equipmentId: uuidSchema }),
  z.object({ kind: z.literal('waterSource'), waterSourceId: uuidSchema }),
])

export type IrrigationProblemSubjectDto = z.infer<
  typeof irrigationProblemSubjectDtoSchema
>

/** The four columns a stored problem uses to say what it is on. */
export interface ProblemSubjectIds {
  zoneId: string | null
  timerId: string | null
  equipmentId: string | null
  waterSourceId: string | null
}

/**
 * The subject those four columns name, or null unless exactly one is set.
 * The converter refuses a row that returns null; the cache schema drops it.
 */
export function problemSubjectFromIds(
  ids: ProblemSubjectIds,
): IrrigationProblemSubjectDto | null {
  const subjects: IrrigationProblemSubjectDto[] = []
  if (ids.zoneId !== null) {
    subjects.push({ kind: 'zone', zoneId: ids.zoneId })
  }
  if (ids.timerId !== null) {
    subjects.push({ kind: 'timer', timerId: ids.timerId })
  }
  if (ids.equipmentId !== null) {
    subjects.push({ kind: 'equipment', equipmentId: ids.equipmentId })
  }
  if (ids.waterSourceId !== null) {
    subjects.push({ kind: 'waterSource', waterSourceId: ids.waterSourceId })
  }
  if (subjects.length !== 1) return null
  const only = subjects[0]
  if (only === undefined) return null
  return only
}

const problemCore = {
  id: uuidSchema,
  siteId: uuidSchema,
  /** Null is "Other", described by `notes`. */
  typeId: uuidSchema.nullable(),
  /** Where, for a problem at a spot in a zone. Null for one on a whole thing. */
  position: z.object(positionFields).nullable(),
  count: z.number().int().positive(),
  severity: problemSeveritySchema.nullable(),
  notes: z.string().nullable(),
  laborMinutes: z.number().int().nonnegative().nullable(),
  materials: z.array(irrigationProblemMaterialDtoSchema),
  /** When the repair was done. Null while the problem is open. */
  fixedAt: z.string().datetime().nullable(),
  foundByName: z.string().nullable(),
  /**
   * The problem on an earlier visit this one was brought forward from by
   * "Still here", or null for one found fresh (docs/site-visits.md).
   */
  previousId: uuidSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}

const irrigationProblemOutSchema = z.object({
  ...problemCore,
  subject: irrigationProblemSubjectDtoSchema,
})

/**
 * A problem cached before it had a subject: four ids, exactly one of them
 * set. Turned into `subject` so a phone's copy still opens, and so the type
 * after parsing is the one shape.
 */
const legacyProblemSchema = z
  .object({
    ...problemCore,
    /**
     * Defaulted for a survey a phone cached before "Still here" existed.
     * The shape this release sends always includes it, so the output schema
     * requires it; only this older shape may omit it.
     */
    previousId: uuidSchema.nullable().default(null),
    zoneId: uuidSchema.nullable(),
    timerId: uuidSchema.nullable(),
    equipmentId: uuidSchema.nullable(),
    waterSourceId: uuidSchema.nullable(),
  })
  .superRefine((problem, context) => {
    if (problemSubjectFromIds(problem) !== null) return
    context.addIssue({
      code: 'custom',
      message: 'A problem is on exactly one thing.',
    })
  })
  .transform((problem) => {
    const subject = problemSubjectFromIds(problem)
    // The refine above already refused this. Returning the cached shape
    // lets the output schema fail it, instead of throwing out of the parse.
    if (subject === null) return problem
    return {
      id: problem.id,
      siteId: problem.siteId,
      typeId: problem.typeId,
      position: problem.position,
      count: problem.count,
      severity: problem.severity,
      notes: problem.notes,
      laborMinutes: problem.laborMinutes,
      materials: problem.materials,
      fixedAt: problem.fixedAt,
      foundByName: problem.foundByName,
      previousId: problem.previousId,
      createdAt: problem.createdAt,
      updatedAt: problem.updatedAt,
      subject,
    }
  })
  .pipe(irrigationProblemOutSchema)

export const irrigationProblemDtoSchema = z.union([
  irrigationProblemOutSchema,
  legacyProblemSchema,
])

export type IrrigationProblemDto = z.infer<typeof irrigationProblemDtoSchema>

/**
 * Everything live in a site's irrigation system: what the survey draws and
 * what a sync answers with. The whole set rather than a diff, as for trees: a
 * phone that has been offline all morning cannot tell what it has missed, so
 * asking "what is true now?" is both simpler and more correct than merging.
 */
export const irrigationSiteDtoSchema = z.object({
  siteId: uuidSchema,
  /**
   * The visit whose problems these are, or null when the site has none for
   * irrigation. The rest of the system is the site's, whatever the visit.
   * Defaulted for a survey cached before visits existed.
   */
  visitId: uuidSchema.nullable().default(null),
  waterSources: z.array(waterSourceDtoSchema),
  timers: z.array(irrigationTimerDtoSchema),
  zones: z.array(zoneDtoSchema),
  equipment: z.array(irrigationEquipmentDtoSchema),
  problems: z.array(irrigationProblemDtoSchema),
})

export type IrrigationSiteDto = z.infer<typeof irrigationSiteDtoSchema>

/**
 * What a site's open repairs come to, priced from the copy of Housecall Pro
 * as it stands: the figure a proposal would start from, before tax. A draft
 * figure, never a quote: sending a proposal freezes its own.
 */
export const irrigationEstimateDtoSchema = z.object({
  /** Everything priced, before tax. Exact decimal string. */
  amount: moneyAmountSchema,
  /** The part of it Housecall Pro marks taxable. */
  taxable: moneyAmountSchema,
  laborMinutes: z.number().int().nonnegative(),
  /**
   * What that labor is billed as: rounded up to the next increment and never
   * under the minimum. More than `laborMinutes` when the minimum applies.
   */
  billedMinutes: z.number().int().nonnegative(),
  /** Open repairs that priced. */
  pricedRepairCount: z.number().int().nonnegative(),
  /** The client's type, which chooses the labor service. */
  clientType: clientTypeSchema,
  labor: z
    .object({
      name: z.string(),
      pricePerHour: moneyAmountSchema,
      taxable: z.boolean(),
    })
    .nullable(),
  /** The site's city as typed, which the drive charge is matched on. */
  city: z.string().nullable(),
  /** The drive charge added: null for no match, or no work to drive to. */
  driveCharge: z
    .object({ town: z.string(), price: moneyAmountSchema })
    .nullable(),
  /** Open repairs that cannot be priced yet, and why. */
  unpriceable: z.array(
    z.object({
      problemId: uuidSchema,
      reason: z.enum(UNPRICEABLE_REASONS),
    }),
  ),
})

export type IrrigationEstimateDto = z.infer<typeof irrigationEstimateDtoSchema>

/** The zone a valve is on, or null for any other kind. */
export function equipmentZoneId(item: IrrigationEquipmentDto): string | null {
  return item.kind === 'VALVE' ? item.zoneId : null
}

/** The timer a master valve or a sensor is wired to, or null otherwise. */
export function equipmentTimerId(item: IrrigationEquipmentDto): string | null {
  return item.kind === 'MASTER_VALVE' || item.kind === 'SENSOR'
    ? item.timerId
    : null
}

/**
 * The water source a master valve, backflow, ball valve, or well component
 * belongs to, or null otherwise.
 */
export function equipmentWaterSourceId(
  item: IrrigationEquipmentDto,
): string | null {
  switch (item.kind) {
    case 'MASTER_VALVE':
    case 'BACKFLOW':
    case 'BALL_VALVE':
    case 'WELL_COMPONENT':
      return item.waterSourceId
    case 'VALVE':
    case 'VALVE_BOX':
    case 'SENSOR':
    case 'OTHER':
      return null
  }
}

/** The valve box something sits in, or null when it stands on its own. */
export function equipmentBoxId(item: IrrigationEquipmentDto): string | null {
  switch (item.kind) {
    case 'VALVE':
    case 'MASTER_VALVE':
    case 'BALL_VALVE':
    case 'SENSOR':
    case 'OTHER':
      return item.boxId
    case 'VALVE_BOX':
    case 'BACKFLOW':
    case 'WELL_COMPONENT':
      return null
  }
}

/** The id of whatever a problem is on, or null for the other three. */
export function problemZoneId(problem: IrrigationProblemDto): string | null {
  return problem.subject.kind === 'zone' ? problem.subject.zoneId : null
}

export function problemTimerId(problem: IrrigationProblemDto): string | null {
  return problem.subject.kind === 'timer' ? problem.subject.timerId : null
}

export function problemEquipmentId(
  problem: IrrigationProblemDto,
): string | null {
  return problem.subject.kind === 'equipment'
    ? problem.subject.equipmentId
    : null
}

export function problemWaterSourceId(
  problem: IrrigationProblemDto,
): string | null {
  return problem.subject.kind === 'waterSource'
    ? problem.subject.waterSourceId
    : null
}
